/**
 * Lightweight i18n system for QuizNudge.
 * No external dependencies — just typed key lookups with interpolation.
 *
 * Usage:
 *   import { t } from '../i18n';
 *   t('nav.quiz')                    // → "Quiz"
 *   t('quiz.questionOf', { current: 1, total: 5 }) // → "Frage 1 von 5"
 */

import { de } from "./locales/de";

type NestedKeyOf<T, Prefix extends string = ""> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object
        ? NestedKeyOf<T[K], `${Prefix}${K}.`>
        : `${Prefix}${K}`;
    }[keyof T & string]
  : never;

export type TranslationKey = NestedKeyOf<typeof de>;

/**
 * Get a translated string by dot-notation key.
 * Supports interpolation: t('key', { name: 'value' }) replaces {name} with value.
 */
export function t(
  key: string,
  params?: Record<string, string | number>,
): string {
  const keys = key.split(".");
  let result: unknown = de;

  for (const k of keys) {
    if (result && typeof result === "object" && k in result) {
      result = (result as Record<string, unknown>)[k];
    } else {
      // Key not found — return key itself as fallback
      console.warn(`[i18n] Missing translation: "${key}"`);
      return key;
    }
  }

  if (typeof result !== "string") {
    console.warn(`[i18n] Key "${key}" is not a string`);
    return key;
  }

  // Interpolation: replace {param} with values
  if (params) {
    return Object.entries(params).reduce(
      (str, [paramKey, paramValue]) =>
        str.replace(new RegExp(`\\{${paramKey}\\}`, "g"), String(paramValue)),
      result,
    );
  }

  return result;
}
